// apps/web/src/components/ScrollRestoration/ScrollRestoration.tsx
// ─────────────────────────────────────────────────────────────────────────
// GLOBAL SCROLL RESTORATION — resets the window to the top on every route
// change, so navigating to a new page never dumps the user partway down the
// previous page's scroll position. Mounted ONCE in AppLayout; no per-page
// wiring. Renders nothing.
//
// RULES (product decision):
//   • Reset fires on PATHNAME change only. Query-string / router-state changes
//     on the same page (a filter toggle, a tab param) do NOT yank the user to
//     the top — that would be jarring mid-interaction.
//   • If the destination URL has a HASH (#mastery, #transcript, …), we do NOT
//     force the top — the user is navigating to a specific section, and the
//     browser's / target page's anchor handling should win.
//   • Reset is INSTANT and PRE-PAINT (useLayoutEffect + behavior:'auto'). A
//     new page is a full swap; a smooth animation there means watching the old
//     page scroll away, which is the jarring thing. Instant = crisp.
//   • Honors prefers-reduced-motion trivially (we're already instant).
//
// The app scrolls on the window (the content area has no inner overflow
// container), so window.scrollTo is the correct target — same assumption the
// ScrollToTop button relies on.
// ─────────────────────────────────────────────────────────────────────────

import React, { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ScrollRestoration: React.FC = () => {
	const { pathname, hash } = useLocation();

	useLayoutEffect(() => {
		// Anchor navigation → let the target section win; don't force the top.
		if (hash) return;

		// Instant, pre-paint reset. `auto` (not `smooth`) so a full page swap
		// lands crisply at the top instead of animating the old content away.
		window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

		// Depend on pathname only: same-page query/state changes must NOT scroll.
		// hash is read for the guard above but intentionally excluded from deps —
		// a hash-only change (same path) shouldn't trigger a top reset either.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname]);

	return null;
};

export default ScrollRestoration;
